import { useState } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';

const INTEGRATION_TYPES = [
  { key: 'SCADA', label: 'SCADA' },
  { key: 'API', label: 'API' },
  { key: 'FTP', label: 'FTP' },
  { key: 'SFTP', label: 'SFTP' },
  { key: 'MQTT', label: 'MQTT' },
  { key: 'manual_upload', label: 'Manual Upload' },
];

interface MeteringIntegration {
  id: string;
  integrationType: string;
  endpointUrl: string;
  protocol: string;
  authType: string;
  status: string;
}

interface Props {
  assetId: string;
  integrations?: MeteringIntegration[];
  onSaved: () => void;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: 'rgba(13,122,60,0.1)', color: '#0D7A3C' },
  inactive: { bg: 'rgba(107,107,112,0.1)', color: '#6B6B70' },
  error: { bg: 'rgba(209,34,41,0.1)', color: '#D12229' },
};

export default function TabMetering({ assetId, integrations = [], onSaved }: Props) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(integrations.length === 0);
  const [form, setForm] = useState({
    integrationType: '',
    endpointUrl: '',
    protocol: '',
    authType: '',
    secretRef: '',
    status: 'active',
  });

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const mutedText = isDark ? '#6B6B70' : '#9B9BA4';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const sectionBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const cardBg = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const tagBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const errorBg = isDark ? 'rgba(209,34,41,0.12)' : 'rgba(209,34,41,0.06)';
  const errorBorder = isDark ? 'rgba(209,34,41,0.25)' : 'rgba(209,34,41,0.2)';

  const handleAdd = async () => {
    if (!form.integrationType || !form.endpointUrl) {
      addToast('Integration type and endpoint URL are required', 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        integrationType: form.integrationType,
        endpointUrl: form.endpointUrl,
        status: form.status,
      };
      if (form.protocol) payload.protocol = form.protocol;
      if (form.authType) payload.authType = form.authType;
      if (form.secretRef) payload.secretRef = form.secretRef;

      await api.post(`/assets/${assetId}/metering-integrations`, payload);
      addToast('Metering integration added successfully', 'success');
      setForm({ integrationType: '', endpointUrl: '', protocol: '', authType: '', secretRef: '', status: 'active' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to add integration');
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-md text-sm transition-colors focus:outline-none focus:ring-1';
  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
            <i className="ri-dashboard-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: textMain }}>Metering / SCADA Integrations</h3>
            <p className="text-xs" style={{ color: textMuted }}>Data collection endpoints and protocols</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
            style={{ border: '1px solid #D12229', color: '#D12229' }}
          >
            <i className="ri-add-line"></i> Add Integration
          </button>
        )}
      </div>

      {/* Existing integrations */}
      {integrations.length > 0 && (
        <div className="space-y-2 mb-5">
          {integrations.map((mi) => {
            const statusStyle = STATUS_COLORS[mi.status] ?? STATUS_COLORS.inactive;
            return (
              <div key={mi.id} className="flex items-center justify-between p-4 rounded-lg" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: iconBg }}>
                    <i className="ri-wifi-line text-sm" style={{ color: textMuted }}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: textMain }}>{mi.integrationType}</span>
                      {mi.protocol && (
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: tagBg, color: textMuted }}>{mi.protocol}</span>
                      )}
                    </div>
                    <div className="text-xs font-mono mt-0.5" style={{ color: textMuted }}>{mi.endpointUrl}</div>
                    {mi.authType && <div className="text-xs mt-0.5" style={{ color: textMuted }}>Auth: {mi.authType}</div>}
                  </div>
                </div>
                <span className="text-xs font-medium px-2.5 py-1 rounded-full capitalize" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                  {mi.status}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="p-5 rounded-xl" style={{ background: sectionBg, border: `1px solid ${sectionBorder}` }}>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold" style={{ color: textMain }}>New Integration</h4>
            {integrations.length > 0 && (
              <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: textMuted }}>
                Cancel
              </button>
            )}
          </div>

          {apiError && (
            <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: errorBg, border: `1px solid ${errorBorder}`, color: '#D12229' }}>
              <i className="ri-error-warning-line mr-2"></i>{apiError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                Integration Type <span style={{ color: '#D12229' }}>*</span>
              </label>
              <select
                value={form.integrationType}
                onChange={(e) => setForm((prev) => ({ ...prev, integrationType: e.target.value }))}
                className={inputClass}
                style={inputStyle}
              >
                <option value="">Select type...</option>
                {INTEGRATION_TYPES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                Endpoint URL <span style={{ color: '#D12229' }}>*</span>
              </label>
              <input
                type="text"
                value={form.endpointUrl}
                onChange={(e) => setForm((prev) => ({ ...prev, endpointUrl: e.target.value }))}
                placeholder="https://scada.example.com/api"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>Protocol</label>
              <input
                type="text"
                value={form.protocol}
                onChange={(e) => setForm((prev) => ({ ...prev, protocol: e.target.value }))}
                placeholder="e.g. Modbus TCP, REST, MQTT"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>Auth Type</label>
              <input
                type="text"
                value={form.authType}
                onChange={(e) => setForm((prev) => ({ ...prev, authType: e.target.value }))}
                placeholder="e.g. API Key, OAuth2, Basic"
                className={inputClass}
                style={inputStyle}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: textMuted }}>
                Secret Reference
                <span className="ml-1 font-normal" style={{ color: textMuted }}>(vault path, not the actual key)</span>
              </label>
              <input
                type="text"
                value={form.secretRef}
                onChange={(e) => setForm((prev) => ({ ...prev, secretRef: e.target.value }))}
                placeholder="vault://helios/asset/scada-api-key"
                className={inputClass}
                style={inputStyle}
              />
              <p className="text-xs mt-1" style={{ color: textMuted }}>
                <i className="ri-shield-keyhole-line mr-1"></i>
                Store only a reference path. Never paste actual API keys here.
              </p>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleAdd}
              disabled={saving || !form.integrationType || !form.endpointUrl}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
              style={{ background: '#D12229' }}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i> Adding...</>
              ) : (
                <><i className="ri-add-line"></i> Add Integration</>
              )}
            </button>
          </div>
        </div>
      )}

      {integrations.length === 0 && !showForm && (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: iconBg }}>
            <i className="ri-wifi-off-line text-xl" style={{ color: textMuted }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>No integrations configured</p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>Add a SCADA, API or other data source</p>
        </div>
      )}
    </div>
  );
}